import { useRef, useState, useEffect } from 'react';

interface UseHorizontalScrollReturn {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  showLeftButton: boolean;
  showRightButton: boolean;
  scrollNext: () => void;
  scrollPrev: () => void;
}

/**
 * Custom hook for horizontal scroll functionality with navigation buttons
 * Handles scroll position tracking, button visibility, and scroll navigation
 */
export const useHorizontalScroll = (
  dependency: unknown[] = [],
  scrollAmount: number = 262
): UseHorizontalScrollReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(true);

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftButton(scrollLeft > 0);
    setShowRightButton(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    // Check initial scroll position
    checkScrollPosition();

    // Add scroll event listener
    scrollElement.addEventListener('scroll', checkScrollPosition);

    // Check on resize
    const handleResize = () => {
      checkScrollPosition();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      scrollElement.removeEventListener('scroll', checkScrollPosition);
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, dependency);

  const scrollNext = () => {
    scrollRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  const scrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  return {
    scrollRef,
    showLeftButton,
    showRightButton,
    scrollNext,
    scrollPrev,
  };
};

